import { useState } from "react";
import { toast } from "sonner";
import { X, Mail, Copy } from "lucide-react";

const SIG = "\n\nWarm regards,\nChris\nSleek Renovations · Sleek Kitchen & Bath\n168 Main Street, Northborough, MA\n(781) 760-4524 · sleekrenovations.com";

const TEMPLATES = {
  cabinetry: {
    label: "Cabinetry & Design",
    subject: "Your cabinetry project — Sleek Kitchen & Bath",
    body: (n) => `Hi ${n},\n\nThanks so much for reaching out about cabinetry and design — you came to the right place. Our Northborough showroom carries premium semi-custom and fully custom lines, and the best first step is seeing the door styles and finishes in person.\n\nWould you like to set up a free design consultation? It takes about 60 minutes: we'll walk the showroom together, talk through how your family uses the space, and start shaping a design around it.\n\nJust reply with a couple of days/times that work for you, or call us directly.${SIG}`,
  },
  remodeling: {
    label: "Full-Service Remodeling",
    subject: "Your remodeling project — Sleek Renovations",
    body: (n) => `Hi ${n},\n\nThank you for reaching out about your remodeling project. With 27+ years of remodeling experience, our team handles everything from design through final finish — one team, one point of accountability.\n\nThe best next step is a quick conversation about your space, timeline, and budget, followed by a visit to our Northborough showroom to look at materials together.\n\nReply with a couple of days/times that suit you, or call us directly and we'll get things moving.${SIG}`,
  },
  both: {
    label: "Cabinetry + Remodel",
    subject: "Your kitchen project — Sleek Kitchen & Bath",
    body: (n) => `Hi ${n},\n\nThanks for reaching out — a full project with our cabinetry is exactly what we do best. We can design your kitchen, supply premium cabinetry from our showroom lines, and build the entire remodel with our own team, start to finish.\n\nLet's begin with a free consultation at our Northborough showroom: we'll walk the displays, gather every detail of how you live, and craft a design that gets it right.\n\nReply with a couple of days/times that work, or give us a call.${SIG}`,
  },
  general: {
    label: "General inquiry",
    subject: "Thanks for reaching out — Sleek Renovations",
    body: (n) => `Hi ${n},\n\nThank you for getting in touch with Sleek Renovations. We'd love to learn more about what you have in mind — whether it's cabinetry, a kitchen or bath remodel, or a whole-home project.\n\nThe easiest next step is a short call or a visit to our Northborough showroom. Reply with a couple of days/times that work for you and we'll take it from there.${SIG}`,
  },
};

const pickTemplate = (projectType) => {
  const t = (projectType || "").toLowerCase();
  if (t.includes("both")) return "both";
  if (t.includes("cabinet")) return "cabinetry";
  if (t.includes("remodel") || t.includes("renovation") || t.includes("kitchen") || t.includes("bath") || t.includes("home")) return "remodeling";
  return "general";
};

const prettyDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
};

const buildTemplates = (lead, isBooking) => {
  if (!isBooking) return TEMPLATES;
  const when = `${prettyDate(lead.preferred_date)} at ${lead.preferred_time}`;
  return {
    confirm: {
      label: "Confirm appointment",
      subject: `You're confirmed — ${when} · Sleek Kitchen & Bath`,
      body: (n) => `Hi ${n},\n\nGreat news — your design consultation is confirmed for ${when} at our Northborough showroom (168 Main Street, Northborough, MA).\n\nPlan for about 60 minutes: we'll walk the showroom together, look at door styles and finishes, and talk through your project in detail. If you have inspiration photos or rough measurements, bring them along — but don't worry if not.\n\nNeed to reschedule? Just reply to this email or call us.${SIG}`,
    },
    reschedule: {
      label: "Suggest new time",
      subject: `About your consultation request · Sleek Kitchen & Bath`,
      body: (n) => `Hi ${n},\n\nThanks for requesting a consultation for ${when}. Unfortunately that slot isn't available — but we'd love to find a time that works.\n\nCould you do one of the following instead?\n\n• [option 1]\n• [option 2]\n\nJust reply with what suits you best, or call us and we'll lock it in.${SIG}`,
    },
    ...TEMPLATES,
  };
};

export const LeadReply = ({ lead, onClose, isBooking = false }) => {
  const firstName = (lead.name || "there").trim().split(/\s+/)[0];
  const templates = buildTemplates(lead, isBooking);
  const initialKey = isBooking ? "confirm" : pickTemplate(lead.project_type);
  const [key, setKey] = useState(initialKey);
  const [subject, setSubject] = useState(templates[initialKey].subject);
  const [body, setBody] = useState(templates[initialKey].body(firstName));

  const switchTemplate = (k) => {
    setKey(k);
    setSubject(templates[k].subject);
    setBody(templates[k].body(firstName));
  };

  const mailto = `mailto:${encodeURIComponent(lead.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Reply copied to clipboard.");
    } catch {
      toast.error("Could not copy — select the text manually.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-6 py-8" data-testid="lead-reply-dialog">
      <div className="bg-white w-full max-w-2xl p-8 relative max-h-full overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-950" data-testid="lead-reply-close" aria-label="Close">
          <X size={18} />
        </button>
        <div className="font-display text-2xl">Reply to {lead.name}</div>
        <div className="text-sm text-zinc-500 mt-1">{lead.email} · {lead.project_type || "no project type"}</div>

        <div className="flex flex-wrap gap-2 mt-5">
          {Object.entries(templates).map(([k, t]) => (
            <button
              key={k}
              onClick={() => switchTemplate(k)}
              className={`text-xs tracking-[0.15em] uppercase px-3 py-2 border transition ${key === k ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-700 hover:border-zinc-950"}`}
              data-testid={`reply-template-${k}`}
            >{t.label}</button>
          ))}
        </div>

        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border border-zinc-300 px-3 py-2.5 mt-5 text-sm focus:outline-none focus:border-zinc-950"
          data-testid="reply-subject-input"
          aria-label="Email subject"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          className="w-full border border-zinc-300 px-3 py-2.5 mt-3 text-sm leading-relaxed focus:outline-none focus:border-zinc-950 resize-y"
          data-testid="reply-body-textarea"
          aria-label="Email body"
        />

        <div className="flex flex-wrap gap-3 mt-5">
          <a
            href={mailto}
            className="flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm hover:bg-zinc-800 transition"
            data-testid="reply-open-email-btn"
          >
            <Mail size={14} /> Open in email app
          </a>
          <button onClick={copy} className="flex items-center gap-2 px-5 py-3 border border-zinc-300 text-sm hover:border-zinc-950 transition" data-testid="reply-copy-btn">
            <Copy size={14} /> Copy text
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadReply;
